import { useMemo } from 'react'
import { unique } from 'lib/array/arrayUtils'
import useOffers from 'hooks/Offers/useOffers'
import { excludeNullOrUndefined } from 'checkout/utils'
import { useAppSelector } from 'hooks/reduxHooks'
import getOfferListKey from 'lib/offer/offerListKey'
import { resolveSearchMapDisplayOffer } from './utils'

export default function useSearchMapResults(
  selectedOfferIds: Array<string>,
  overrideOfferList?: App.OfferList,
  pageSize: number = 20,
  offerList?: App.OfferList,
  filters: App.OfferListFilters = {},
  enableStreamMetaPins: boolean = false,
) {
  const offerMetaDataKey = getOfferListKey(filters)
  const streamMetaByOfferId = useAppSelector(
    state => state.offer.searchResultMetadata.offerMetaData[offerMetaDataKey],
  )

  const offerIds = useMemo(() => {
    if (!enableStreamMetaPins) {
      if (!offerList?.fetching && overrideOfferList) {
        return unique([...overrideOfferList.offerIds, ...selectedOfferIds])
      }

      return []
    }

    const paginatedOfferIds = overrideOfferList?.offerIds ?? []

    if (paginatedOfferIds.length === 0 && selectedOfferIds.length === 0) {
      return []
    }

    if (offerList?.fetching && paginatedOfferIds.length === 0) {
      return unique(selectedOfferIds)
    }

    return unique([...paginatedOfferIds, ...selectedOfferIds])
  }, [enableStreamMetaPins, offerList?.fetching, overrideOfferList, selectedOfferIds])

  const [loadedOffers, offersFetching] = useOffers<App.HotelOffer | App.BedbankOffer>(offerIds)

  const offers = useMemo(() => {
    const loadedOfferMap = new Map(loadedOffers.map(offer => [offer.id, offer]))

    if (!enableStreamMetaPins) {
      return offerIds.map(id => loadedOfferMap.get(id)).filter(excludeNullOrUndefined)
    }

    return offerIds.map(offerId => resolveSearchMapDisplayOffer(
      loadedOfferMap.get(offerId),
      streamMetaByOfferId?.[offerId],
    )).filter(excludeNullOrUndefined)
  }, [enableStreamMetaPins, loadedOffers, offerIds, streamMetaByOfferId])

  const totalNumOffers = overrideOfferList?.offerCount || 0
  const paginatedOfferCount = overrideOfferList?.offerIds?.length ?? 0

  const fetchingList = (offerList?.offerIds?.length || 0) < totalNumOffers
  // Only treat the current page as "still loading" while the offer list is actively
  // fetching. Otherwise a partial page (e.g. 15/20) with a large unfinished stream
  // keeps the "Loading offers" banner stuck even though markers are already shown.
  const fetchingPage = paginatedOfferCount < pageSize &&
    fetchingList &&
    !overrideOfferList?.streamEnded &&
    !!offerList?.fetching
  const error = overrideOfferList?.error && !overrideOfferList?.streamEnded
  const offersReadyForPins = enableStreamMetaPins && offerIds.length > 0 && offers.length === offerIds.length
  const streamMetaOffersLoaded = offerIds.length === 0 ||
    (offersReadyForPins && !offersFetching && loadedOffers.length === offerIds.length)
  const allOffersLoaded = enableStreamMetaPins
    ? !fetchingList && streamMetaOffersLoaded
    : !fetchingList && !offersFetching && offers.length === totalNumOffers
  const noOffersAvailable = !fetchingList && totalNumOffers === 0
  const key = overrideOfferList?.key

  const offersInteractable = enableStreamMetaPins ? offersReadyForPins : allOffersLoaded

  return useMemo(() => ({
    offers,
    totalNumOffers,
    fetchingList,
    error,
    allOffersLoaded,
    offersReadyForPins,
    offersInteractable,
    noOffersAvailable,
    key,
    fetchingPage,
  }), [
    offers,
    totalNumOffers,
    fetchingList,
    error,
    allOffersLoaded,
    offersReadyForPins,
    offersInteractable,
    noOffersAvailable,
    key,
    fetchingPage,
  ])
}
